import { Lock, Person, Visibility, VisibilityOff } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useLogin from "../../hooks/useLogin";
import styles from "./Acceso.module.css";
import backgroundImage from "../../assets/background.svg";

const Acceso = () => {
    const theme = useTheme();
    const [formData, setFormData] = useState({
        username: "",
        password: "",
    });
    const [showPassword, setShowPassword] = useState(false);

    const { login, loading, error, clearError } = useLogin();
    const navigate = useNavigate();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Limpiar error cuando el usuario empiece a escribir
        if (error) clearError();
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validaciones básicas
        if (!formData.username.trim() || !formData.password.trim()) {
            return;
        }

        const result = await login(formData.username, formData.password);

        if (result.success) {
            // Redirigir al dashboard o página principal
            navigate("/dashboard");
        }
    };

    return (
        <Box
            className={styles.container}
            style={{ backgroundImage: `url(${backgroundImage})` }}
        >
            <Stack
                className={styles.card}
                style={{
                    "--backgroundColor": theme.palette.background.paper,
                }}
            >
                {/* Logo o título */}
                <Stack
                    spacing={1}
                    direction={"column"}
                    className={styles.titleHeader}
                >
                    <Typography
                        component="h1"
                        variant="h4"
                        className={styles.title}
                        color={theme.palette.text.primary}
                    >
                        Acceso
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Ingresa tus credenciales para continuar
                    </Typography>
                </Stack>

                {/* Formulario */}
                <Stack
                    direction={"column"}
                    spacing={2}
                    width={"100%"}
                    component="form"
                    onSubmit={handleSubmit}
                >
                    {/* Campo de usuario */}
                    <TextField
                        label="Usuario"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        required
                        autoFocus
                        autoComplete="username"
                        disabled={loading}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />

                    {/* Campo de contraseña */}
                    <TextField
                        label="Contraseña"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        autoComplete="current-password"
                        disabled={loading}
                        showPassword={showPassword}
                        onTogglePassword={handleTogglePasswordVisibility}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment:
                                    handleTogglePasswordVisibility && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={
                                                    handleTogglePasswordVisibility
                                                }
                                                edge="end"
                                                disabled={loading}
                                            >
                                                {showPassword ? (
                                                    <VisibilityOff />
                                                ) : (
                                                    <Visibility />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                            },
                        }}
                    />

                    {/* Mensaje de error */}
                    {error && <Alert severity="error">{error}</Alert>}

                    {/* Botón de envío */}
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading}
                        className={styles.button}
                    >
                        {loading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "Iniciar Sesión"
                        )}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
};

export default Acceso;
